import type { SetupItemDetail } from "@/types/setup-checklist";

export type SetupFilter = "critical" | "required";

export interface SetupSection {
  /** Registry group key, stable across label renames. */
  key: string;
  name: string;
  items: SetupItemDetail[];
  openCount: number;
  settledCount: number;
  doneCount: number;
  skippedCount: number;
  criticalOpenCount: number;
  isComplete: boolean;
  /** Its place in the guide, which holds after finished sections sink. */
  position: number;
}

export function groupBySection(items: SetupItemDetail[]): SetupSection[] {
  const buckets = new Map<string, SetupItemDetail[]>();
  const labels = new Map<string, string>();

  for (const item of items) {
    const key = item.definition.group;
    labels.set(key, item.definition.groupLabel);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const sections = Array.from(buckets, ([key, groupItems], position) => {
    // Sink settled last, or dependency order pulls finished prerequisites back up.
    const ordered = sinkSettled(orderWithinGroup(groupItems));
    const openCount = ordered.filter((i) => i.status === "OPEN").length;
    const settled = ordered.filter((i) => i.status !== "OPEN");
    return {
      key,
      name: labels.get(key) ?? key,
      items: ordered,
      openCount,
      settledCount: settled.length,
      doneCount: settled.filter((i) => i.status === "DONE").length,
      skippedCount: settled.filter((i) => i.status === "SKIPPED").length,
      criticalOpenCount: ordered.filter(
        (i) => i.status === "OPEN" && i.definition.critical,
      ).length,
      isComplete: openCount === 0,
      position,
    };
  });

  return [
    ...sections.filter((s) => !s.isComplete),
    ...sections.filter((s) => s.isComplete),
  ];
}

function sinkSettled(items: SetupItemDetail[]): SetupItemDetail[] {
  return [
    ...items.filter((item) => item.status === "OPEN"),
    ...items.filter((item) => item.status !== "OPEN"),
  ];
}

/** Sorts a group, then places each item after its prerequisites. */
function orderWithinGroup(items: SetupItemDetail[]): SetupItemDetail[] {
  const sorted = [...items].sort(compareItems);
  const byKey = new Map(sorted.map((i) => [i.itemKey, i]));

  const placed: SetupItemDetail[] = [];
  const done = new Set<string>();

  const place = (item: SetupItemDetail, visiting: Set<string>) => {
    if (done.has(item.itemKey) || visiting.has(item.itemKey)) return;
    visiting.add(item.itemKey);
    for (const need of item.definition.dependsOn) {
      const prereq = byKey.get(need);
      if (prereq) place(prereq, visiting);
    }
    if (!done.has(item.itemKey)) {
      done.add(item.itemKey);
      placed.push(item);
    }
  };

  for (const item of sorted) place(item, new Set());
  return placed;
}

function compareItems(a: SetupItemDetail, b: SetupItemDetail): number {
  return a.definition.order - b.definition.order;
}

/** Titles of an item's prerequisites that are open and in the same group. */
export function prerequisiteTitles(
  item: SetupItemDetail,
  section: SetupSection,
): string[] {
  if (item.definition.dependsOn.length === 0) return [];
  return section.items
    .filter(
      (other) =>
        other.status === "OPEN" &&
        item.definition.dependsOn.includes(other.itemKey),
    )
    .map((other) => other.definition.title);
}

export function matchesFilter(
  item: SetupItemDetail,
  filter: SetupFilter | null,
): boolean {
  if (filter === "critical") return item.definition.critical;
  if (filter === "required") return item.definition.importance === "MANDATORY";
  return true;
}

export function countOpen(
  items: SetupItemDetail[],
  where: (definition: SetupItemDetail["definition"]) => boolean,
): number {
  return items.filter(
    (item) => item.status === "OPEN" && where(item.definition),
  ).length;
}

export function firstOpen(
  items: SetupItemDetail[],
): SetupItemDetail | undefined {
  return items.find((item) => item.status === "OPEN");
}

/** The next open item in a section after `itemId`, wrapping to the top. */
export function nextOpenItem(
  section: SetupSection,
  itemId: string,
): SetupItemDetail | undefined {
  const index = section.items.findIndex((item) => item.id === itemId);
  return (
    firstOpen(section.items.slice(index + 1)) ??
    firstOpen(section.items.filter((item) => item.id !== itemId))
  );
}

/** The next unfinished section after `key` in guide order, wrapping to the top. */
export function nextUnfinishedSection(
  sections: SetupSection[],
  key: string,
): SetupSection | undefined {
  const from = sections.find((section) => section.key === key)?.position ?? -1;
  const unfinished = sections
    .filter((section) => !section.isComplete && section.key !== key)
    .sort((a, b) => a.position - b.position);
  return unfinished.find((section) => section.position > from) ?? unfinished[0];
}

/** The full page's query string for a view: the expanded item, else the open section, plus the filter. */
export function setupGuideQuery(view: {
  items: SetupItemDetail[];
  expandedItem: string | null;
  section: string | null;
  filter: SetupFilter | null;
}): string {
  // An item that's in a closed section or filtered out would reopen on Back, so it's left out.
  const shown = view.items.find((item) => item.id === view.expandedItem);
  const itemId =
    shown &&
    shown.definition.group === view.section &&
    matchesFilter(shown, view.filter)
      ? shown.id
      : null;
  const params = new URLSearchParams();
  if (itemId) params.set("item", itemId);
  else if (view.section) params.set("section", view.section);
  if (view.filter) params.set("filter", view.filter);
  return params.toString();
}
